import { useEffect, useRef, useState } from 'react';
import { TCFClientBuilder } from '@skyscanner-internal/transparency-consent-framework-client';
import reactReplacePlaceholder from 'react-replace-placeholder';
import observer from 'saddlebag-observer';
import { acknowledgeTrackingSettings, getPreferences, isGdprInitialised, PREFERENCES, setInitialPreferences, setPreferences, showCookieBanner } from 'saddlebag-tracking-preferences';
import { BpkButtonV2 as BpkButton, BUTTON_TYPES } from '@skyscanner/backpack-web/bpk-component-button';
import BpkLink from '@skyscanner/backpack-web/bpk-component-link';
import BpkText, { TEXT_STYLES } from '@skyscanner/backpack-web/bpk-component-text';
import { withScrimmedPortal } from '@skyscanner/backpack-web/bpk-scrim-utils';
import StringsPropVerification from '../higher-order-components/StringsPropVerification';
import { getErrorMessage } from '../utils/error-helpers/getErrorMessage';
import { trackEventFn as defaultTrackEventFn } from '../utils/tracking';
import { cookieBannerTrackingEvent } from './userTrackingEvents';
import parseActiveVendorList from './utils/parseActiveVendorList';
import STYLES from "./CookieBanner.module.css";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const stringsKeys = ['title', 'explainYourOptions', 'close', 'manageOptions', 'consentInformationSectionA', 'consentInformationSectionB', 'consentInformationSectionD'];
const optionalStringsKeys = ['declineAllSectionA', 'declineAllSectionB', 'declineAllSectionC', 'declineAllButtonAcceptAll', 'declineAllButtonManageSettings', 'declineAllButtonAcceptEssentialOnly', 'body'];
const componentName = 'cookie-banner';
const saveGDPRPreferences = ({
  accepted = true,
  logger,
  tcString = ''
}) => {
  try {
    acknowledgeTrackingSettings();
    setPreferences({
      [PREFERENCES.INFORMATION]: accepted,
      [PREFERENCES.ADVERTS]: accepted,
      ...(tcString && {
        [PREFERENCES.TC_STRING]: tcString
      })
    });
  } catch (error) {
    logger.logWarn({
      message: `Saving GDPR Preferences failed: ${getErrorMessage(error)}`,
      level: 'warn',
      component: componentName
    });
  }
};
const mbdTrackingParams = name => {
  const cookieBannerComponentTrackingParams = {
    component_name: name
  };
  return JSON.stringify(cookieBannerComponentTrackingParams);
};
const contextCookiePolicyLink = (logger, cookiePolicyUrl, trackEventFn) => ({
  tag1: {
    pair: 'tag2',
    format: children => _jsx(BpkLink, {
      href: cookiePolicyUrl,
      onClick: () => {
        trackEventFn(cookieBannerTrackingEvent('Link Clicked'));
        logger.logOperationalEvent({
          eventName: 'Link Clicked',
          component: componentName
        });
      },
      "data-tracking-element-id": "cookie_banner_policy_link",
      "data-tracking-common-params": mbdTrackingParams('cookie_banner_policy_link'),
      id: "cookiePolicyLink",
      children: children
    }, "cookiePolicyLink")
  },
  tag3: {
    pair: 'tag4',
    format: children => _jsx(BpkLink, {
      href: "/privacy-settings#vendors",
      onClick: () => {
        trackEventFn(cookieBannerTrackingEvent('Vendor Link Clicked'));
        logger.logOperationalEvent({
          eventName: 'Vendor Link Clicked',
          component: componentName
        });
      },
      "data-tracking-element-id": "cookie_banner_policy_vendor_link",
      "data-tracking-common-params": mbdTrackingParams('cookie_banner_policy_vendor_link'),
      id: "thirdPartyVendorsLink",
      children: children
    }, "thirdPartyVendorsLink")
  },
  tag5: {
    pair: 'tag6',
    format: children => _jsx(BpkLink, {
      href: "/privacy-settings",
      onClick: () => {
        trackEventFn(cookieBannerTrackingEvent('Privacy Settings Link Clicked'));
        logger.logOperationalEvent({
          eventName: 'Privacy Settings Link Clicked',
          component: componentName
        });
      },
      id: "privacySettingsLink",
      children: children
    }, "privacySettingsLink")
  }
});
const CookieBannerContent = ({
  activeVendorList = '',
  cookiePolicyUrl = '/cookiepolicy.aspx',
  dialogRef,
  logger,
  market,
  onPolicyAcceptClick,
  strings,
  totalNumberOfVendors = null,
  trackEventFn = defaultTrackEventFn,
  transparencyConsentFrameworkConfigVersionNumber = ''
}) => {
  var _strings$declineAllSe;
  const cookieHeaderRef = useRef(null);
  useEffect(() => {
    const cookieBanner = document.getElementById('cookieBannerContent');
    if (cookieBanner) {
      const {
        display
      } = window.getComputedStyle(cookieBanner);
      if (display === 'none') {
        prepareAndSaveGDPRPreferences({
          accepted: false
        });
        trackEventFn(cookieBannerTrackingEvent('Declined'));
        logger.logOperationalEvent({
          eventName: 'Declined',
          component: componentName
        });
      }
    }
  }, []);
  useEffect(() => {
    var _cookieHeaderRef$curr;
    (_cookieHeaderRef$curr = cookieHeaderRef.current) === null || _cookieHeaderRef$curr === void 0 || _cookieHeaderRef$curr.focus();
  }, []);
  const [trackingPreferencesChangedSubscription, setTrackingPreferencesChangedSubscription] = useState(null);
  useEffect(() => {
    setTrackingPreferencesChangedSubscription(observer.subscribe('tracking-preferences-changed', () => {
      if (!showCookieBanner(market)) {
        logger.logOperationalEvent({
          eventName: 'Dismissed',
          component: componentName
        });
        onPolicyAcceptClick(null);
      }
    }));
  }, [onPolicyAcceptClick, market, logger]);
  useEffect(() => {
    try {
      if (!isGdprInitialised()) {
        setInitialPreferences(market);
      }
    } catch (error) {
      logger.logWarn({
        message: `setInitialPreferences failed: ${getErrorMessage(error)}`,
        component: componentName
      });
    }
    trackEventFn(cookieBannerTrackingEvent('Visible'));
    logger.logOperationalEvent({
      eventName: 'Visible',
      component: componentName
    });
  }, [logger, market, trackEventFn]);
  let tcfClient;
  let activeVendors;
  if (activeVendorList) {
    activeVendors = parseActiveVendorList(activeVendorList);
  }
  try {
    tcfClient = TCFClientBuilder.build({
      forConsentScreen: true,
      activeVendorList: activeVendors,
      tcfConfigVersionNumber: transparencyConsentFrameworkConfigVersionNumber
    });
  } catch (error) {
    const errorStack = error instanceof Error ? error.stack : '';
    logger.logWarn({
      message: `Unable to initialise TCF on Cookie Banner: ${errorStack}`,
      level: 'warn',
      component: componentName
    });
    logger.logOperationalEvent({
      eventName: 'TCF: Unable to initialise on Cookie Banner',
      component: componentName
    });
  }
  function prepareAndSaveGDPRPreferences({
    accepted = true
  }) {
    if (!tcfClient) {
      saveGDPRPreferences({
        accepted,
        logger
      });
      return;
    }
    let tcString = '';
    const acceptAllOrDeclineAllVendorsMethod = accepted ? 'acceptAllVendors' : 'declineAllVendors';
    tcfClient[acceptAllOrDeclineAllVendorsMethod]().then(() => {
      tcString = tcfClient.getEncodedTCString();
      tcfClient.close();
    }).catch(error => {
      logger.logWarn({
        message: `Unable to ${accepted ? 'accept' : 'decline'} all vendors and persist TC String: ${error.stack}`,
        level: 'warn',
        component: componentName
      });
      logger.logOperationalEvent({
        eventName: `TCF: ${accepted ? 'Accept' : 'Decline'} All Vendors failure`,
        component: componentName
      });
    }).finally(() => {
      saveGDPRPreferences({
        accepted,
        logger,
        tcString
      });
    });
  }
  const waitForPreferencesSaved = () => new Promise(resolve => {
    observer.subscribe('tracking-preferences-changed', () => {
      if (getPreferences(market)[PREFERENCES.INFORMATION]) {
        resolve();
      }
    });
  });
  const prefChangeNewRelicAndExpTrigger = async () => {
    await waitForPreferencesSaved();
    logger.logOperationalEvent({
      eventName: 'Accepted',
      component: componentName
    });
  };
  return _jsx("div", {
    ref: dialogRef,
    className: STYLES['cookie-banner'],
    id: "cookieBannerContent",
    children: _jsxs("div", {
      className: STYLES['cookie-banner__wrapper'],
      "data-tracking-element-id": "cookie_banner",
      "data-tracking-common-params": mbdTrackingParams('cookie_banner'),
      children: [_jsx("img", {
        className: STYLES['cookie-banner__image'],
        alt: "",
        width: "80",
        height: "100",
        src: "https://content.skyscnr.com/m/67dcbbe580384179/original/Cookie-Promise.svg"
      }), _jsxs("div", {
        tabIndex: -1,
        ref: cookieHeaderRef,
        className: STYLES['cookie-banner__message'],
        "data-testid": "cookieBannerMessage",
        children: [_jsx(BpkText, {
          tagName: "h2",
          textStyle: TEXT_STYLES.heading3,
          children: strings.title
        }), _jsx(BpkText, {
          tagName: "p",
          textStyle: TEXT_STYLES.sm,
          children: reactReplacePlaceholder(strings.declineAllSectionA, contextCookiePolicyLink(logger, cookiePolicyUrl, trackEventFn))
        }), _jsx(BpkText, {
          tagName: "p",
          textStyle: TEXT_STYLES.sm,
          className: STYLES['cookie-banner__explain-options'],
          children: reactReplacePlaceholder((_strings$declineAllSe = strings.declineAllSectionB) === null || _strings$declineAllSe === void 0 ? void 0 : _strings$declineAllSe.replace('@@totalNumberOfVendors@@', totalNumberOfVendors || ''), contextCookiePolicyLink(logger, cookiePolicyUrl, trackEventFn))
        }), _jsx(BpkText, {
          tagName: "p",
          textStyle: TEXT_STYLES.sm,
          className: STYLES['cookie-banner__explain-options'],
          children: reactReplacePlaceholder(strings.declineAllSectionC, contextCookiePolicyLink(logger, cookiePolicyUrl, trackEventFn))
        })]
      }), _jsxs("div", {
        className: STYLES['cookie-banner__buttons'],
        children: [_jsx(BpkButton, {
          className: [STYLES['cookie-banner__button'], STYLES['cookie-banner__button-accept']].join(' '),
          onClick: clickEvent => {
            observer.unsubscribe(trackingPreferencesChangedSubscription);
            prepareAndSaveGDPRPreferences({
              accepted: true
            });
            trackEventFn(cookieBannerTrackingEvent('Accepted'));
            prefChangeNewRelicAndExpTrigger();
            observer.publish('cookie-policy-accepted');
            onPolicyAcceptClick(clickEvent);
          },
          "data-tracking-element-id": "cookie_banner_accept_all",
          "data-tracking-common-params": mbdTrackingParams('cookie_banner_accept_all'),
          id: "acceptCookieButton",
          children: strings.declineAllButtonAcceptAll
        }), _jsx(BpkButton, {
          type: BUTTON_TYPES.secondary,
          className: [STYLES['cookie-banner__button'], STYLES['cookie-banner__button-manage']].join(' '),
          href: "/privacy-settings",
          onClick: () => {
            trackEventFn(cookieBannerTrackingEvent('Manage Preferences Clicked'));
            logger.logOperationalEvent({
              eventName: 'Manage Preferences Clicked',
              component: componentName
            });
          },
          "data-tracking-element-id": "cookie_banner_manage_settings",
          "data-tracking-common-params": mbdTrackingParams('cookie_banner_manage_settings'),
          children: strings.declineAllButtonManageSettings
        }), _jsx(BpkButton, {
          type: BUTTON_TYPES.secondary,
          className: [STYLES['cookie-banner__button'], STYLES['cookie-banner__button-accept-essential-only']].join(' '),
          onClick: () => {
            prepareAndSaveGDPRPreferences({
              accepted: false
            });
            trackEventFn(cookieBannerTrackingEvent('Declined'));
            logger.logOperationalEvent({
              eventName: 'Declined',
              component: componentName
            });
          },
          "data-tracking-element-id": "cookie_banner_essential_only",
          "data-tracking-common-params": mbdTrackingParams('cookie_banner_essential_only'),
          children: strings.declineAllButtonAcceptEssentialOnly
        })]
      })]
    })
  });
};
const WrappedCookieBannerContent = withScrimmedPortal(CookieBannerContent);
const CookieBanner = props => _jsx(StringsPropVerification, {
  componentName: CookieBanner.name,
  isProductionEnv: props.isProductionEnv || false,
  logger: props.logger,
  strings: props.strings,
  stringsKeys: stringsKeys,
  children: _jsx(WrappedCookieBannerContent, {
    ...props,
    closeOnScrimClick: false,
    getApplicationElement: () => document.querySelector('#pagewrap'),
    renderTarget: () => document.getElementById('modal-container'),
    containerClassName: "cookie-banner-wrapper"
  })
});
export default CookieBanner;